/**
 * Графики частоты Wordstat — inline SVG, без библиотек: 24 точки не стоят
 * чарт-движка в бандле.
 *
 * Цвета — ординальная лента `--chart-1..3` из токенов (прогнана валидатором
 * dataviz): прошлые 12 месяцев — бледный шаг, последние 12 — сильный. Так
 * «этот год против прошлого» читается без легенды-расшифровки, а легенда всё
 * равно есть (два ряда → легенда обязательна). Текст — текстовыми токенами,
 * никогда цветом ряда.
 */
import { useMemo, useState } from "react";

import { num } from "@/lib/media";
import { monthLong, monthShort, type MonthPoint } from "@/lib/trends";

/** Спарклайн 24 месяцев в строке таблицы. Масштаб — свой у каждой строки: важна форма, не величина. */
export function Sparkline({ months, width = 120, height = 28 }: { months: MonthPoint[]; width?: number; height?: number }) {
  if (months.length < 2) return <span className="text-[11.5px] text-fg-faint">нет истории</span>;
  const max = Math.max(...months.map((m) => m.count), 1);
  const pad = 3;
  const step = (width - pad * 2) / (months.length - 1);
  const y = (v: number) => height - pad - (v / max) * (height - pad * 2);
  const pts = months.map((m, i) => [pad + i * step, y(m.count)] as const);
  const split = Math.max(0, months.length - 12);
  const path = (from: number, to: number) =>
    pts
      .slice(from, to)
      .map(([x, yy], i) => `${i ? "L" : "M"}${x.toFixed(1)},${yy.toFixed(1)}`)
      .join("");
  const last = pts[pts.length - 1]!;
  const peak = months.reduce((best, m) => (m.count > best.count ? m : best), months[0]!);
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block overflow-visible"
      role="img"
      aria-label={`24 месяца, пик ${monthLong(peak.month)}: ${num(peak.count)}`}
    >
      <title>{`${monthShort(months[0]!.month)} — ${monthShort(months[months.length - 1]!.month)} · пик ${monthShort(peak.month)}: ${num(peak.count)}`}</title>
      {/* Прошлый год — бледный, этот — сильный; стык общий, чтобы линия не рвалась. */}
      <path d={path(0, split + 1)} fill="none" stroke="var(--chart-1)" strokeWidth={1.5} strokeLinejoin="round" />
      <path d={path(split, pts.length)} fill="none" stroke="var(--chart-3)" strokeWidth={2} strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill="var(--chart-3)" stroke="var(--bg)" strokeWidth={1} />
    </svg>
  );
}

/**
 * Столбики 24 месяцев на экране типа: подпись каждого месяца («окт 24»),
 * значение — по наведению. Цель наведения — вся колонка, а не тонкий столбик:
 * в низкий месяц иначе не попасть.
 */
export function MonthBars({ months }: { months: MonthPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 960;
  const H = 220;
  const top = 22;
  const bottom = 34;
  const left = 44; // поле шкалы: подписи сетки не должны прятаться под столбиками
  const right = 8;
  const plotH = H - top - bottom;
  const max = useMemo(() => Math.max(...months.map((m) => m.count), 1), [months]);
  if (!months.length) return null;
  const slot = (W - left - right) / months.length;
  const barW = Math.max(4, slot - 4); // 2 px поверхности с каждой стороны — зазор между столбиками
  const split = Math.max(0, months.length - 12);
  const base = top + plotH;
  const shown = hover !== null ? months[hover] : null;

  // Сетка: три линии по «круглым» значениям — фон, не сигнал.
  const ticks = niceTicks(max);

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-4 text-[12px] text-fg-muted">
        <Legend color="var(--chart-1)" text="прошлые 12 мес" />
        <Legend color="var(--chart-3)" text="последние 12 мес" />
        <span className="ml-auto min-h-[18px] text-fg tnum">
          {shown ? (
            <>
              <span className="text-fg-muted">{monthLong(shown.month)}:</span> {num(shown.count)}
            </>
          ) : (
            <span className="text-fg-faint">наведите на месяц</span>
          )}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Частота запроса по месяцам за 24 месяца"
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => {
          const yy = base - (t / max) * plotH;
          return (
            <g key={t}>
              <line x1={left} x2={W - right} y1={yy} y2={yy} stroke="var(--border-c)" strokeDasharray="2 4" />
              <text x={left - 6} y={yy + 3} textAnchor="end" fontSize={10} fill="var(--fg-faint)">
                {num(t)}
              </text>
            </g>
          );
        })}
        <line x1={left} x2={W - right} y1={base} y2={base} stroke="var(--border-strong-c)" />
        {months.map((m, i) => {
          const h = (m.count / max) * plotH;
          const x = left + i * slot + (slot - barW) / 2;
          const on = hover === i;
          return (
            <g key={m.month} onMouseEnter={() => setHover(i)}>
              {/* Цель наведения — вся колонка. */}
              <rect x={left + i * slot} y={top} width={slot} height={plotH + bottom} fill="transparent" />
              <path
                d={roundedTop(x, base, barW, h, Math.min(4, barW / 2))}
                fill={i >= split ? "var(--chart-3)" : "var(--chart-1)"}
                opacity={hover === null || on ? 1 : 0.55}
              />
              {on ? (
                <text
                  x={x + barW / 2}
                  y={Math.max(11, base - h - 6)}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--fg)"
                >
                  {num(m.count)}
                </text>
              ) : null}
              <text
                x={x + barW / 2}
                y={base + 14}
                textAnchor="middle"
                fontSize={10}
                fill={on ? "var(--fg)" : "var(--fg-faint)"}
              >
                {monthShort(m.month).split(" ")[0]}
              </text>
              {/* Год — под январём и под первым месяцем ряда, иначе подписи сливаются. */}
              {i === 0 || m.month.slice(5, 7) === "01" ? (
                <text x={x + barW / 2} y={base + 27} textAnchor="middle" fontSize={10} fill="var(--fg-muted)">
                  {m.month.slice(0, 4)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Legend({ color, text }: { color: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block size-2.5 rounded-[2px]" style={{ background: color }} />
      {text}
    </span>
  );
}

/** Столбик со скруглённым верхом, низ — прямой по базовой линии. */
function roundedTop(x: number, base: number, w: number, h: number, r: number): string {
  if (h <= 0) return "";
  const rr = Math.min(r, h);
  const y = base - h;
  return `M${x},${base}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${base}Z`;
}

/** Две-три «круглые» отметки сетки ниже максимума. */
function niceTicks(max: number): number[] {
  const raw = max / 3;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 2.5, 5, 10].map((k) => k * pow).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = unit; v < max; v += unit) out.push(v);
  return out;
}
